import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Question,
  QuestionType,
  GetImportanceColorFn,
} from './types';
import { toUploadUrl } from '../../utils/uploadUrl';
import MathTextRenderer from '../shared/MathTextRenderer';

interface ViewQuestionDialogProps {
  open: boolean;
  question: Question | null;
  onClose: () => void;
  getImportanceColor: GetImportanceColorFn;
}

const ViewQuestionDialog = memo<ViewQuestionDialogProps>(({
  open,
  question,
  onClose,
  getImportanceColor,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Question Details</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {question && (
          <Box>
            <Box display="flex" gap={1} mb={2}>
              <Chip label={question.type} variant="outlined" />
              <Chip
                label={question.importance}
                color={getImportanceColor(question.importance)}
              />
            </Box>

            <Typography variant="h6" gutterBottom>
              Question:
            </Typography>
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
              <MathTextRenderer text={question.questionText} />
            </Typography>
            {question.questionImages && question.questionImages.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {question.questionImages.map((filename) => (
                  <Box
                    key={filename}
                    component="img"
                    src={toUploadUrl('question-images', filename)}
                    alt="Question attachment"
                    sx={{
                      maxWidth: 120,
                      maxHeight: 120,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      objectFit: 'contain',
                      bgcolor: 'background.paper',
                    }}
                  />
                ))}
              </Box>
            )}

            {question.type === QuestionType.MCQ && question.options && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Options:
                </Typography>
                {question.options.map((option, index) => (
                  <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                    {String.fromCharCode(65 + index)}. <MathTextRenderer text={option} />
                  </Typography>
                ))}
                {question.correctAnswer && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Correct Answer: <MathTextRenderer text={question.correctAnswer} />
                  </Alert>
                )}
              </Box>
            )}

            {question.type === QuestionType.FILL_IN_THE_BLANKS &&
              question.correctAnswer && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Correct Answer: <MathTextRenderer text={question.correctAnswer} />
                </Alert>
              )}

            {(question.type === QuestionType.SHORT ||
              question.type === QuestionType.LONG) && (
              <>
                {question.subParts && question.subParts.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sub-parts:
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      {question.subParts.map((part, index) => {
                        const roman = ['i','ii','iii','iv','v','vi','vii','viii','ix','x','xi','xii','xiii','xiv','xv'];
                        return (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main', mr: 1 }}>
                              ({roman[index] || index + 1})
                            </Box>
                            <MathTextRenderer text={part.text} />
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {(question.answer || (question.answerImages && question.answerImages.length > 0)) && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Answer:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      {question.answer && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          <MathTextRenderer text={question.answer} />
                        </Typography>
                      )}
                      {question.answerImages && question.answerImages.length > 0 && (
                        <Box sx={{ mt: question.answer ? 1.5 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {question.answerImages.map((filename) => (
                            <Box
                              key={filename}
                              component="img"
                              src={toUploadUrl('answer-images', filename)}
                              alt="Answer attachment"
                              sx={{
                                maxWidth: 160,
                                maxHeight: 160,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                objectFit: 'contain',
                                bgcolor: 'background.paper',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )}
              </>
            )}

            {question.metadata && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Metadata:
                </Typography>
                <Grid container spacing={1}>
                  {question.metadata.marks && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Marks:</strong> {question.metadata.marks}
                      </Typography>
                    </Grid>
                  )}
                  {question.metadata.difficulty && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Difficulty:</strong> {question.metadata.difficulty}
                      </Typography>
                    </Grid>
                  )}
                  {question.metadata.estimatedTime && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Est. Time:</strong> {question.metadata.estimatedTime} min
                      </Typography>
                    </Grid>
                  )}
                  {question.metadata.tags && question.metadata.tags.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Tags:</strong>
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {question.metadata.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {question.metadata.pastPaperReferences &&
                    question.metadata.pastPaperReferences.length > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Past Paper References:</strong>
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {question.metadata.pastPaperReferences.map((ref, index) => (
                            <Chip key={index} label={ref} size="small" variant="outlined" color="info" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
});

ViewQuestionDialog.displayName = 'ViewQuestionDialog';

export default ViewQuestionDialog;
